/**
 * DesignButtonMenu.tsx - Donation Button Styling Component
 *
 * This component provides customization options for donation button appearance
 * including style types, colors, and visual effects.
 *
 * Key Features:
 * - Button style type selection (solid, gradient, outline)
 * - Custom color configuration for button theming
 * - Real-time button preview with applied styles
 * - Color picker integration for precise color selection
 * - Validation for color combinations and accessibility
 * - Integration with overall theme colors
 *
 * Button Style Options:
 * - Solid: Single color background with contrasting text
 * - Gradient: Two-color gradient background for visual appeal
 * - Outline: Transparent background with colored border
 *
 * Color Configuration:
 * - Primary Color: Main button background or border color
 * - Secondary Color: Gradient end color or accent elements
 * - Text Color: Button text color with contrast validation
 *
 * Props: None (uses MobX store for state management)
 *
 * Dependencies: Color picker, button preview, MobX store integration
 */

import React, { useState } from "react";
import styled from "styled-components";
import { IDonationButtonColors } from "../../../interfaces";
import { ColorModel, FundraiserDonationButtonColors } from "../../../models";
import { SelectedColorButton } from "../../gallery-settings/GalleryAppearanceSection";
import {
  FlexContainer,
  ColorPicker as ColorPickerComponent,
  IColor,
} from "@mindmemobile/mm-shared-global";

const SubMenuContent = styled.div`
  padding: 8px 12px 12px 12px;
  background: #ffffff;

  .checkout-color-picker {
    position: absolute !important;
    top: auto;
    left: auto;
    transform: translate(-1px, 170px);
    z-index: 9999 !important;
    #rbgcp-wrapper {
      // this is for the height width of the color picker box inside the color picker component
      > div:first-child > div:nth-child(2) > div:nth-child(2) {
        height: 150px !important;
      }
    }
  }
`;

const ButtonTypeContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 24px;
`;

const ButtonTypeCard = styled.button<{ selected: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  border-radius: 12px;
  border: 2px solid ${(props) => (props.selected ? "#3B82F6" : "#E2E8F0")};
  background: #ffffff;
  cursor: pointer;
  transition: all 0.2s ease;
  width: 100%;
  min-height: 54px;

  &:hover {
    border-color: #3b82f6;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
`;

const ButtonTypeName = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  flex: 1;
  text-align: left;
`;

const ColorFieldContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ColorField = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const ColorFieldLabel = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  min-width: 80px;
  text-align: left;
`;

const ColorInputContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  .text-color-picker {
    transform: translate(53px, 60px);
  }
  .primary-btn-color-picker {
    transform: translate(53px, 0px);
  }
  .secondary-btn-color-picker {
    transform: translate(53px, -55px);
  }
  #rbgcp-wrapper {
    // this is for the height width of the color picker box inside the color picker component

    > div:first-child > div:first-child {
      height: 165px !important;
    }
  }
`;

const ColorPickerInput = styled.input`
  width: 32px;
  height: 32px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  cursor: pointer;
  background: transparent;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &::-webkit-color-swatch-wrapper {
    padding: 0;
  }

  &::-webkit-color-swatch {
    border: none;
    border-radius: 6px;
  }
`;

const HexInput = styled.input<{ hasError?: boolean }>`
  padding: 8px 12px;
  border: 1px solid ${(props) => (props.hasError ? "#EF4444" : "#E2E8F0")};
  border-radius: 8px;
  font-size: 14px;
  font-family: monospace;
  color: #1e293b;
  width: 100px;
  background: ${(props) => (props.hasError ? "#FEF2F2" : "#FFFFFF")};

  &:focus {
    outline: none;
    border-color: ${(props) => (props.hasError ? "#EF4444" : "#3B82F6")};
    box-shadow: 0 0 0 2px
      ${(props) =>
        props.hasError ? "rgba(239, 68, 68, 0.1)" : "rgba(59, 130, 246, 0.1)"};
  }
`;

interface DesignButtonMenuProps {
  donationButtonType: string;
  donationButtonColors: FundraiserDonationButtonColors;
  onDonationButtonTypeChange: (type: string) => void;
  onDonationButtonColorChange: (
    colorType: keyof FundraiserDonationButtonColors,
    value: ColorModel
  ) => void;
}

export const DesignButtonMenu: React.FC<DesignButtonMenuProps> = ({
  donationButtonType,
  donationButtonColors,
  onDonationButtonTypeChange,
  onDonationButtonColorChange,
}) => {
  const [activePicker, setActivePicker] = useState<string | null>(null);

  const handleColorChange = (
    key: keyof FundraiserDonationButtonColors,
    color: IColor
  ) => {
    const newColor = new ColorModel(color);
    onDonationButtonColorChange(key, newColor);
  };

  return (
    <SubMenuContent>
      {/* Button Type Selection */}
      <ButtonTypeContainer>
        <ButtonTypeCard
          selected={donationButtonType === "solid"}
          onClick={() => onDonationButtonTypeChange("solid")}
        >
          <ButtonTypeName>Solid</ButtonTypeName>
        </ButtonTypeCard>

        <ButtonTypeCard
          selected={donationButtonType === "gradient"}
          onClick={() => onDonationButtonTypeChange("gradient")}
        >
          <ButtonTypeName>Gradient</ButtonTypeName>
        </ButtonTypeCard>
      </ButtonTypeContainer>

      {/* Color Configuration */}
      <ColorFieldContainer>
        {/* Text Color */}
        <ColorField>
          <ColorFieldLabel>Text Color</ColorFieldLabel>
          <ColorInputContainer>
            <SelectedColorButton
              color={
                donationButtonColors.text.rgba || donationButtonColors.text.hex
              }
              onClick={() =>
                setActivePicker(activePicker === "text" ? null : "text")
              }
              className="color-picker-gallery"
            >
              <div />
            </SelectedColorButton>

            <HexInput
              type="text"
              value={donationButtonColors.text.hex}
              onChange={(e) =>
                handleColorChange(
                  "text",
                  new ColorModel({
                    hex: e.target.value,
                    rgba: e.target.value,
                  })
                )
              }
              placeholder="#ffffff"
              onClick={(e) => e.stopPropagation()}
            />

            {activePicker === "text" && (
              <ColorPickerComponent
                color={donationButtonColors.text}
                onChange={(color) => handleColorChange("text", color)}
                onHide={() => setActivePicker(null)}
                className="text-color-picker checkout-color-picker"
              />
            )}
          </ColorInputContainer>
        </ColorField>

        {/* Button Color (Solid) or Start Color (Gradient) */}
        <ColorField>
          <ColorFieldLabel>
            {donationButtonType === "solid"
              ? "Button Color"
              : "Button Start Color"}
          </ColorFieldLabel>
          <ColorInputContainer>
            <SelectedColorButton
              color={
                donationButtonColors.primary.rgba ||
                donationButtonColors.primary.hex
              }
              onClick={() =>
                setActivePicker(activePicker === "primary" ? null : "primary")
              }
              className="color-picker-gallery"
            >
              <div />
            </SelectedColorButton>

            <HexInput
              type="text"
              value={donationButtonColors.primary.hex}
              onChange={(e) =>
                handleColorChange(
                  "primary",
                  new ColorModel({
                    hex: e.target.value,
                    rgba: e.target.value,
                  })
                )
              }
              placeholder=""
              onClick={(e) => e.stopPropagation()}
            />

            {activePicker === "primary" && (
              <ColorPickerComponent
                color={donationButtonColors.primary}
                onChange={(color) => handleColorChange("primary", color)}
                onHide={() => setActivePicker(null)}
                className="primary-btn-color-picker checkout-color-picker"
              />
            )}
          </ColorInputContainer>
        </ColorField>

        {/* End Color (Gradient only) */}
        {donationButtonType === "gradient" && (
          <ColorField>
            <ColorFieldLabel>Button End Color</ColorFieldLabel>
            <ColorInputContainer>
              <SelectedColorButton
                color={
                  donationButtonColors.secondary.rgba ||
                  donationButtonColors.secondary.hex
                }
                onClick={() =>
                  setActivePicker(
                    activePicker === "secondary" ? null : "secondary"
                  )
                }
                className="color-picker-gallery"
              >
                <div />
              </SelectedColorButton>

              <HexInput
                type="text"
                value={donationButtonColors.secondary.hex}
                onChange={(e) =>
                  handleColorChange(
                    "secondary",
                    new ColorModel({
                      hex: e.target.value,
                      rgba: e.target.value,
                    })
                  )
                }
                placeholder=""
                onClick={(e) => e.stopPropagation()}
              />

              {activePicker === "secondary" && (
                <ColorPickerComponent
                  color={donationButtonColors.secondary}
                  onChange={(color) => handleColorChange("secondary", color)}
                  onHide={() => setActivePicker(null)}
                  className="secondary-btn-color-picker checkout-color-picker"
                />
              )}
            </ColorInputContainer>
          </ColorField>
        )}
      </ColorFieldContainer>
    </SubMenuContent>
  );
};
